import { AxiosError } from 'axios';
import Link from 'next/link';

import { APIErrorResponseGenerator } from '@/lib/helper';

import Button from '@/components/@base/button';
import { VARIANT_ENUM } from '@/components/@base/button/type';
import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import XImage from '@/components/@base/x-image';
import HistoryItem from '@/components/app/product/components/side-bar/purchase-history/history-item';
import Props from '@/components/app/product/components/side-bar/purchase-history/type';
import { useGetAddOnsInfo, useGetDownloadLink } from '@/components/app/product/services/hooks';
import ZhaketCustomAccordion from '@/components/shared/components/zhaket-custom-accordion';

import imagesList from '@/constant/images-list';
import localization, { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function PurchaseHistory({ slug, productId }: Props) {
  const { data: addonsData, isPending: isAddonsDataPending } = useGetAddOnsInfo({
    slug,
  });
  const {
    refetch: refetchDownloadLink,
    isLoading: isGetDownloadLinkPending,
    isFetching,
  } = useGetDownloadLink(productId);

  if (!addonsData || !addonsData.isProductBuyer) {
    return null;
  }

  const handleDownloadClick = () => {
    const fetchDownloadLink = async () => {
      refetchDownloadLink()
        .then((response) => {
          if (response.data) {
            const link = document.createElement('a');
            link.href = response.data;
            link.setAttribute('download', ''); // Optional: you can specify a default filename here
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            showToast({ message: 'Download link is not available', type: 'error' });
          }
        })
        .catch((error) => {
          if (error instanceof AxiosError) {
            showToast(APIErrorResponseGenerator(error));
          }
        });
    };

    fetchDownloadLink();
  };

  const PurchaseHistoryList = (
    <>
      {addonsData.purchaseHistory.slice(0, 10).map((item) => (
        <HistoryItem
          className='py-3 first:pt-0 last:pb-7'
          key={item.id}
          {...item}
          productId={productId}
        />
      ))}
      <Button
        Icon={
          <XImage
            alt='download'
            src={imagesList.icons.downloadIconWhite}
            width={16}
            height={16}
          />
        }
        className='h-[44px] w-full bg-[#FF9606] hover:bg-[#EB8800]'
        onClick={handleDownloadClick}
        isLoading={isFetching}
      >
        {productLocalization.productDownload}
      </Button>
      {addonsData.purchaseHistory.length > 10 && (
        <Link href={`/panel/downloads?s=${slug}`}>
          <Button
            variant={VARIANT_ENUM.OUTLINED}
            className='my-4 h-[44px] w-full border-[#EB8800] bg-[#FFFFFF] text-[#EB8800] hover:border-[#B47510] hover:bg-[#FFE6C0] hover:text-[#B47510]'
          >
            {localization.viewAll}
          </Button>
        </Link>
      )}
    </>
  );

  return (
    <Container>
      <ZhaketCustomAccordion
        buttonIcon={
          <XImage
            alt='download'
            src={imagesList.icons.downloadIconOrange}
            width={24}
            height={24}
          />
        }
        buttonText={productLocalization.yourPurchaseHistory}
        panelContent={PurchaseHistoryList}
      />
    </Container>
  );
}
